/**
 * SignedOutScreen — the view users land on when an authenticated
 * request returns 401. Replaces the generic ErrorScreen's 401
 * branch because that generic view used alarming chrome (danger
 * triangle + technical "Bearer token invalid or expired" text +
 * a "Try again" button that immediately re-401s) and made the
 * end-user feel like something had failed.
 *
 * Design intent:
 *
 *   - Tone is calm + reassuring. A session ending is a routine
 *     security event, not a fault.
 *
 *   - Copy is CAUSE-AGNOSTIC. The backend deliberately returns a
 *     uniform 401 for every reason (idle timeout, expiry,
 *     admin-revoked, policy change, unknown token) to avoid
 *     leaking enumeration signal. Earlier revisions claimed
 *     "your session ended after a period of inactivity" — honest
 *     for the common path but a lie when a platform admin
 *     force-revoked the user mid-audit. The current message just
 *     names the outcome and the fix, without speculating on why.
 *
 *   - Single clear primary action: Sign in. A hard navigation
 *     (<a href>) is deliberate — it resets any stale in-memory
 *     auth state that the SPA might be holding and triggers a
 *     fresh bundle parse against the freshly-cleared session
 *     cookie.
 *
 *   - Technical details (request ID) live behind a collapsed
 *     "Need help?" disclosure. Support reps can walk the user
 *     through expanding it when a ticket needs the id; end users
 *     who just want to continue don't see SDK vocabulary.
 *
 *   - Focus moves to the primary action on mount so a keyboard
 *     user can press Enter without navigating. The container is
 *     role="alert" so assistive tech announces the state change
 *     without falsely implying modal semantics.
 */

import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, ChevronUp, Copy, LockKeyhole } from "lucide-react";
import { Button } from "../button";

export interface SignedOutScreenProps {
  /**
   * Where the "Sign in" button navigates. When omitted, the
   * component builds "/login?redirect=<current-pathname+search>"
   * at mount so the user lands back on the deep link they were
   * on before the session ended. The caller can override — e.g.
   * to force the root path on a kiosk surface where returning
   * to the prior page is undesired.
   *
   * Redirect hygiene here is only the first gate: we forward
   * pathname + search but never host or hash, so the built
   * value IS same-origin by construction. The /login page
   * validates the `redirect` search param via auth-core's
   * `safeRedirectPath` / `safeSameOriginRedirect` before
   * navigating — that's where the open-redirect defence lives.
   */
  loginHref?: string;

  /**
   * Correlation id surfaced in the collapsed "Need help?"
   * disclosure. Omit when unavailable — the disclosure still
   * shows with whatever signal is present.
   */
  requestId?: string | null;

  /**
   * Override the explanation. Defaults to the inactivity-oriented
   * copy. Exposed for i18n and for surfaces (admin console on a
   * shared kiosk) that want stronger shared-device wording.
   */
  message?: string;

  /**
   * Override the headline. Default is the consumer-neutral
   * "You've been signed out" — consistent across admin + portal.
   */
  title?: string;
}

const DEFAULT_TITLE = "You've been signed out";
const DEFAULT_MESSAGE =
  "Your session has ended. Sign in again to continue.";

export function SignedOutScreen({
  loginHref,
  requestId,
  message,
  title,
}: SignedOutScreenProps) {
  const primaryRef = useRef<HTMLAnchorElement>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  // Computed in an effect so the "current URL" capture is SSR-safe.
  // Default redirect preserves pathname + search; never host (same-
  // origin enforced by shape) and never hash (keeps any memory-only
  // tokens out of the new URL).
  const [resolvedHref, setResolvedHref] = useState<string>(loginHref ?? "/login");
  useEffect(() => {
    if (loginHref) {
      setResolvedHref(loginHref);
      return;
    }
    if (typeof window === "undefined") return;
    const here = window.location.pathname + window.location.search;
    // Don't round-trip a user who was already on /login — that
    // would produce ?redirect=/login, which the login page would
    // interpret as "go back to login after login" = loop.
    if (here === "/login" || here.startsWith("/login?")) {
      setResolvedHref("/login");
      return;
    }
    setResolvedHref("/login?redirect=" + encodeURIComponent(here));
  }, [loginHref]);

  // Focus the primary action on mount so keyboard users can
  // Enter-to-signin without tabbing. Screen readers announce the
  // role="alert" container (implicit aria-live="assertive") and
  // the focused link text together.
  useEffect(() => {
    primaryRef.current?.focus();
  }, []);

  async function copyRequestId() {
    if (!requestId) return;
    try {
      await navigator.clipboard.writeText(requestId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Silent — clipboard failures are a best-effort nicety,
      // not a feature the user depends on.
    }
  }

  return (
    <div
      className="min-h-[60vh] flex items-center justify-center p-6"
      // role="alert" is an implicit aria-live="assertive"
      // aria-atomic="true" region — the whole title +
      // description + primary-action is announced together when
      // the screen replaces the failed content. Accurate for a
      // disruptive state change; does NOT falsely imply there's
      // inert content behind the way role="alertdialog" +
      // aria-modal="true" would.
      role="alert"
      aria-labelledby="signed-out-title"
      aria-describedby="signed-out-desc"
    >
      <div className="w-full max-w-md space-y-6 text-center">
        <div className="mx-auto h-14 w-14 rounded-xl border border-primary/30 bg-primary/10 flex items-center justify-center">
          <LockKeyhole className="h-7 w-7 text-primary" aria-hidden />
        </div>
        <div className="space-y-2">
          <h1
            id="signed-out-title"
            className="text-2xl font-bold tracking-tight"
          >
            {title ?? DEFAULT_TITLE}
          </h1>
          <p
            id="signed-out-desc"
            className="text-sm text-muted-foreground leading-relaxed"
          >
            {message ?? DEFAULT_MESSAGE}
          </p>
        </div>

        <div className="flex flex-col items-stretch gap-2 max-w-xs mx-auto">
          {/* Hard navigation (<a> not Link) is deliberate: clears
              any SPA-held auth state and forces a fresh bundle
              parse against the cleared session cookie.
              `asChild` collapses the Button's styling onto the
              anchor so we have ONE focusable element, not a
              focusable <a> wrapping a focusable <button>. */}
          <Button asChild size="lg" className="w-full">
            <a ref={primaryRef} href={resolvedHref}>
              Sign in
            </a>
          </Button>
        </div>

        {/* "Need help?" disclosure. Collapsed by default because
            end users who just want to continue shouldn't see
            implementation vocabulary. Support expands it when a
            ticket needs the request id. */}
        {requestId ? (
          <div className="pt-2">
            <button
              type="button"
              onClick={() => setDetailsOpen((v) => !v)}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              aria-expanded={detailsOpen}
              aria-controls="signed-out-details"
            >
              {detailsOpen ? (
                <ChevronUp className="h-3 w-3" aria-hidden />
              ) : (
                <ChevronDown className="h-3 w-3" aria-hidden />
              )}
              Need help?
            </button>
            {detailsOpen && (
              <div
                id="signed-out-details"
                className="ui-card mt-3 rounded-lg border border-border/60 bg-card p-3 text-left text-xs space-y-2"
              >
                <p className="text-muted-foreground">
                  If you reach out to support, share the reference
                  below so they can find this event in the audit log.
                </p>
                <div className="flex items-center gap-2">
                  <span className="uppercase tracking-wider text-muted-foreground">
                    Reference
                  </span>
                  <code className="flex-1 font-mono text-foreground break-all">
                    {requestId}
                  </code>
                  <button
                    type="button"
                    onClick={copyRequestId}
                    className="p-1 text-muted-foreground hover:text-foreground"
                    aria-label={copied ? "Copied" : "Copy reference"}
                    title={copied ? "Copied" : "Copy reference"}
                  >
                    {copied ? (
                      <Check className="h-3.5 w-3.5 text-success" aria-hidden />
                    ) : (
                      <Copy className="h-3.5 w-3.5" aria-hidden />
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
